import lodash from 'lodash';
import mongoose from 'mongoose';
import {
    call, put, takeEvery, select, all,
} from 'redux-saga/effects';
import { message } from 'antd';
import { getLeadProfile, getLeadProfileVariant } from '../../requests/get_lead_profile_request';
import { getLeadProfileRequestSuccess, getLeadProfileRequestFailure } from '../actions/get_lead_profile.actions';
import {
    GET_LEAD_PROFILE_REQUEST,
    SET_REF_BIKE_LEAD_REDUCER,
    SET_REF_LEAD_CUSTOMER_REDUCER,
    SET_MOT_HISTORY_REDUCER,
    SET_MARKETING_REDUCER,
    SET_CAP_VALUATION_REDUCER,
    SET_LEAD_NOTES_REDUCER,
} from '../constants/get_lead_profile.constants';
import { getExistingStock } from '../../requests/cap_requests/get_existing_stock_request';
import { getBikeHpiCheck } from '../../requests/bike_hpi_check_request';
import { readNextLead, readPreviousLead } from '../../requests/read_next_prev_lead_request';
import { getBlemishReport } from '../../requests/leads_questions/get_blemish_report_request';
import { getLeadsDuplicateByLeadId } from '../../requests/get_bike_duplicates_request';
import { getUserPermissions } from '../../../../App/requests/check_permission_request';
import { leadFinanceSchema } from '../../../LeadPaymentApproval/schemas/lead_finance.schema';
import { SET_LEAD_PAYMENT_APPROVAL_DETAILS_DATA, GET_LEAD_PAYMENT_APPROVAL_DETAILS_DATA } from '../../../LeadPaymentApproval/constants';
import { getAllowedDivisionIds, buildFiltersForLeadsReq, clearingFiltersForLeadReq } from '../../../Manage_retail/utils/buildFiltersForLeadsReq';
import { getLeadConversation } from './get_lead_conversation.saga';
import { getLeadDisposition } from './get_lead_disposition.saga';
import buildAsyncFiltersForLeads from '../../../shared/sagas/buildAsyncFiltersForLeads.saga';
import { checkIsLeadManageDeliveryRequested } from '../../../Manage_retail/utils';

const HIDE_DATA_FOR_SOURCES = [
    // Leadomatic
    '5be93e5f683059f77dbcef4c',
];

const REQUIRED_PERM = {
    DEL_NOTES: 'method://Leads/deleteLeadNote',
};

function* resetStates() {
    const leadFinanceModel = mongoose.Document({}, leadFinanceSchema);

    yield put({
        type: 'SET_LEAD_DATA',
        payload: {
            lead_profile_data_loading: true,
            vrmDuplicates: { isLoading: true, vrmsList: [] },
            pagination: {
                previous_lead_id: null,
                next_lead_id: null,
                isLoading: true,
            },
        },
    });
    yield put({ type: 'SET_EXISTING_STOCK_DATA', payload: { isLoading: true } });
    yield put({ type: 'SET_BLEMISH_REPORT', payload: { blemish_report_loading: true } });
    yield put({ type: 'SET_LEAD_HISTORY_DATA', payload: { data: [], error: null, isLoading: true, isLeadTypeUpdate: false } });
    yield put({
        type: SET_LEAD_PAYMENT_APPROVAL_DETAILS_DATA,
        payload: {
            isLoading: true,
            dataModel: leadFinanceModel,
        },
    });
    yield put({ type: 'RESET_BIKE_DETAILS' });
}

function* setMainLeadInfo(leadData) {
    yield put(getLeadProfileRequestSuccess(leadData));
    yield put({ type: 'SET_LEAD_PROFILE_PHOTOS', payload: { loading: false, photos: leadData.photos || [] } });
    yield put({
        type: SET_REF_BIKE_LEAD_REDUCER,
        payload: leadData.ref_bike_lead,
        customer_collection_date: leadData.customer_collection_date,
        part_ex: leadData.part_ex,
        photos: leadData.photos,
        has_completed_blemish: leadData.has_completed_blemish,
        completed_blemish_step: leadData.completed_blemish_step,
        lead_pot: leadData.lead_pot,
        is_manager_special: leadData.is_manager_special,
        use_live_price: leadData.use_live_price,
        served_live_price: leadData.served_live_price,
        vin_check: leadData.vin_check,
    });
    yield put({
        type: SET_REF_LEAD_CUSTOMER_REDUCER,
        payload: { ...leadData.ref_lead_customer, customer_collection_date: leadData.customer_collection_date },
    });
    yield put({ type: SET_MARKETING_REDUCER, payload: { marketing: leadData.marketing, created_by: leadData.created_by, ref_lead_source: leadData.ref_lead_source } });
    yield put({ type: SET_MOT_HISTORY_REDUCER, payload: leadData.ref_bike_lead.mot_history });
    yield put({ type: SET_CAP_VALUATION_REDUCER, payload: { cap_valuation: leadData.ref_bike_lead.cap_valuation, cap_valuation_history: leadData.ref_bike_lead.cap_valuation_history } });
    yield put({ type: SET_LEAD_NOTES_REDUCER, payload: leadData.notes });
    yield put({
        type: 'SET_LEAD_VALUATIONS',
        payload: {
            loading: false,
            valuations: leadData.valuations.reverse(),
            customer_valuation: leadData.customer_valuation,
            customer_valuation_date: leadData.customer_valuation_date,
            selling_reason: leadData.selling_reason,
            close_reason: leadData.close_valuation_reason,
        },
    });
}

// HPI CHECK & LEAD FINANCE & BLEMISH REPORT Features
function* getBlemishHpiFinancePriceLookup(leadData, timers) {
    const hpiFinanceBlemishPriceResTimerStart = Date.now();
    const priceLookupPayload = {
        price_look_up_loading: false,
        price_look_up: null, // unused
        error: null,
    };
    const blemishHpiFinancePriceCalls = {
        blemishReportRes: call(getBlemishReport, leadData._id),
        hpiCheckRes: call(getBikeHpiCheck, leadData.ref_bike_lead._id),
    };

    const {
        blemishReportRes, hpiCheckRes,
    } = yield all(blemishHpiFinancePriceCalls);

    yield put({
        type: GET_LEAD_PAYMENT_APPROVAL_DETAILS_DATA,
        leadId: leadData._id,
        valuationsFromProfile: leadData.valuations,
        blemQ2: blemishReportRes.options.questions.question_2,
        leadCustomer: leadData.ref_lead_customer,
    });

    timers.push({ timer: 'hpiFinanceBlemishPriceResTimerStart', cat: 'main', time: Date.now() - hpiFinanceBlemishPriceResTimerStart });

    yield put({
        type: 'SET_BLEMISH_REPORT',
        payload: {
            blemish_report_loading: false,
            blemish_report: blemishReportRes.options,
            error: blemishReportRes.error,
        },
    });
    yield put({
        type: 'SET_LEAD_DATA',
        payload: {
            bike_hpi_check: hpiCheckRes.options,
            error: hpiCheckRes.error,
            ...priceLookupPayload,
            lead_profile_data_loading: false,
        },
    });
}

// EXTENDING BIKE VARIANT Feature
function* getExtBikeVarial(leadData, timers) {
    const model_id = lodash.get(leadData, 'ref_bike_lead.ref_bike_model._id');
    const variant_id = lodash.get(leadData, 'ref_bike_lead.ref_bike_variant._id');
    let year = lodash.get(leadData, 'ref_bike_lead.ref_bike_variant.year');

    if (year === undefined || year === null) {
        year = lodash.get(leadData, 'ref_bike_lead.unmaped_data.bike_year');
    }

    const extBikeVarResTimerStart = Date.now();
    const extBikeVarRes = yield call(getLeadProfileVariant, model_id, variant_id, year);
    timers.push({ timer: 'extBikeVarRes', cat: 'additional', time: Date.now() - extBikeVarResTimerStart });

    if (extBikeVarRes.result && lodash.get(extBikeVarRes, 'options.length')) {
        lodash.set(leadData, 'ref_bike_lead.ref_bike_variant', extBikeVarRes.options[0]);
    }
}

function* getDupBikes(leadData) {
    const leadSource = leadData.ref_lead_source._id;

    if (!HIDE_DATA_FOR_SOURCES.includes(leadSource) && leadData.ref_bike_lead.vrm) {
        const duplicatesLeadsRes = yield call(getLeadsDuplicateByLeadId, leadData._id);

        if (duplicatesLeadsRes.result && lodash.get(duplicatesLeadsRes, 'options.length')) {
            yield put({
                type: 'SET_LEAD_DATA',
                payload: {
                    vrmDuplicates: {
                        vrmsList: lodash.isArray(duplicatesLeadsRes.options) ? duplicatesLeadsRes.options : [],
                        isLoading: false,
                    },
                },
            });
        } else {
            yield put({ type: 'SET_LEAD_DATA', payload: { vrmDuplicates: { isLoading: false, vrmsList: [] } } });
        }
    } else {
        yield put({ type: 'SET_LEAD_DATA', payload: { vrmDuplicates: { isLoading: false, vrmsList: [] } } });
    }
}

// DUPLICATES & EXISTING STOCK & PREV-NEXT Lead Features
function* getDuplicatesStockPrevNext(leadData, timers) {
    const store = yield select();
    const divisionsIds = getAllowedDivisionIds(store);
    const { filters } = store.Leads.Leads_manage;
    const filtersForLeadsReq = buildFiltersForLeadsReq(filters, divisionsIds);
    const where = yield buildAsyncFiltersForLeads(filtersForLeadsReq);
    const cleanFilters = clearingFiltersForLeadReq(where);
    const leadSource = leadData.ref_lead_source._id;
    const dupStockPrevNextResTimerStart = Date.now();
    const isDeliveryRequested = checkIsLeadManageDeliveryRequested();

    const dupStockPrevNextCalls = {
        nextLeadRes: call(readNextLead, isDeliveryRequested ? leadData.transport_requested_date : leadData.created_date, leadData._id, cleanFilters, isDeliveryRequested),
        prevLeadRes: call(readPreviousLead, isDeliveryRequested ? leadData.transport_requested_date : leadData.created_date, leadData._id, cleanFilters, isDeliveryRequested),
        dupBikes: call(getDupBikes, leadData, timers),
    };

    if (!HIDE_DATA_FOR_SOURCES.includes(leadSource) && lodash.get(leadData, 'ref_bike_lead.ref_bike_model._id')) {
        dupStockPrevNextCalls.inStockRes = call(getExistingStock, leadData.ref_bike_lead.ref_bike_model._id);
    }

    const { nextLeadRes, prevLeadRes, inStockRes } = yield all(dupStockPrevNextCalls);
    timers.push({ timer: 'dupStockPrevNextResTimerStart', cat: 'additional', time: Date.now() - dupStockPrevNextResTimerStart });

    if (inStockRes) {
        yield put({
            type: 'SET_EXISTING_STOCK_DATA',
            payload: {
                isLoading: false,
                existingStock: inStockRes.result ? inStockRes.options : [],
                error: inStockRes.error,
            },
        });
    }

    yield put({
        type: 'SET_LEAD_DATA',
        payload: {
            pagination: {
                previous_lead_id: lodash.get(prevLeadRes, 'options._id') || null,
                next_lead_id: lodash.get(nextLeadRes, 'options._id') || null,
                isLoading: false,
            },
        },
        error: nextLeadRes.error || prevLeadRes.error,
    });
}

function* getLeadProfileRequest({ _id }) {
    const timers = [];
    const getLeadProfileRequestStartTimer = Date.now();
    const delNoteAllowed = yield select((state) => lodash.get(state, `App.Permissions.allowedUrls.${REQUIRED_PERM.DEL_NOTES}`));

    yield call(resetStates);

    if (delNoteAllowed === undefined) {
        const permRes = yield call(getUserPermissions, ['method://Leads/deleteLeadNote'], ['x']);
        const allowed = permRes.options.allowedUrls[REQUIRED_PERM.DEL_NOTES];

        if (permRes.result) {
            yield put({
                type: 'SET_PERMISSIONS_DATA',
                payload: {
                    [`allowedUrls.${REQUIRED_PERM.DEL_NOTES}`]: allowed || false,
                },
            });
        }
    }

    const getLeadProfileTimerStart = Date.now();
    const {
        result, options: leadData, msg, error,
    } = yield call(getLeadProfile, _id);
    timers.push({ timer: 'getLeadProfile', cat: 'main', time: Date.now() - getLeadProfileTimerStart });

    if (result && leadData) {
        // --------------------------- MAIN LEAD INFO ------------------------------
        yield call(setMainLeadInfo, leadData);
        yield call(getBlemishHpiFinancePriceLookup, leadData, timers);
        yield call(getLeadConversation, leadData._id);
        yield call(getLeadDisposition, leadData._id);
        // --------------------------- ADDITIONAL LEAD INFO ------------------------------
        yield call(getExtBikeVarial, leadData, timers);
        yield call(getDuplicatesStockPrevNext, leadData, timers);
        yield put({ type: 'GET_LEAD_HISTORY_REQ', leadId: leadData._id });

        const timersResult = timers.reduce((initial, curVal) => {
            initial[curVal.cat] += curVal.time;
            return initial;
        }, {
            main: 0,
            additional: 0,
        });
        console.log('timersTotal', timersResult, `timersSum: ${timersResult.main + timersResult.additional}`);
        console.log('getLeadProfileRequestStartTimer', Date.now() - getLeadProfileRequestStartTimer);
    } else {
        console.error('Error loading the lead');
        console.log(result, leadData, msg, error);
        message.error('Error while loading lead details, please contact admin');
        yield put(getLeadProfileRequestFailure(error));
        yield put({ type: 'SET_LEAD_DATA', payload: { lead_profile_data_loading: false, error } });
    }
}

export default function* getLeadProfileSaga() {
    yield takeEvery(GET_LEAD_PROFILE_REQUEST, getLeadProfileRequest);
}



// WEBPACK FOOTER //
// ./src/modules/Leads/Lead_profile/redux/sagas/get_lead_profile.saga.js